import React from "react";

import {
  asArray,
  aa,
  formatLabel,
  formatNumber,
} from "./adminAnalyticsUtils";

export function EmptyState({ title, body, actionLabel, onAction }) {
  return (
    <section className="admin-analytics-state">
      <h2>{title}</h2>
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? (
        <button
          className="admin-analytics-state__action"
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}

export function SummaryGrid({ cards }) {
  const safeCards = asArray(cards);

  return (
    <section
      className="admin-analytics-summary-grid"
      aria-label={aa("summary_aria", "Summary")}
    >
      {safeCards.map((card) => (
        <article className="admin-analytics-summary-card" key={card.label}>
          <span>{card.label}</span>
          <strong>{card.value}</strong>
          {card.detail ? <small>{card.detail}</small> : null}
        </article>
      ))}
    </section>
  );
}

export function Panel({ title, value, children, className = "", emptyMessage }) {
  return (
    <section className={`admin-analytics-panel ${className}`.trim()}>
      {title || value ? (
        <div className="admin-analytics-panel__header">
          {title ? <h2>{title}</h2> : <span />}
          {value ? <strong>{value}</strong> : null}
        </div>
      ) : null}
      {children || (
        <p className="admin-analytics-panel__empty">
          {emptyMessage || aa("no_data_for_period", "No data for this period.")}
        </p>
      )}
    </section>
  );
}

export function UnavailableTablesNotice({ tables }) {
  const safeTables = asArray(tables).filter(Boolean);
  if (safeTables.length === 0) return null;

  return (
    <section className="admin-analytics-warning">
      <strong>
        {aa(
          "unavailable_tables",
          "Some analytics tables were unavailable."
        )}
      </strong>
      <span>{safeTables.join(", ")}</span>
    </section>
  );
}

export function MetricListPanel({ title, rows, keyName = "label", valueName = "value" }) {
  const safeRows = asArray(rows);

  return (
    <Panel title={title}>
      {safeRows.length > 0 ? (
        <div className="admin-analytics-metric-list">
          {safeRows.map((row) => (
            <div
              className="admin-analytics-metric-list__row"
              key={`${title}-${row?.[keyName] || row?.label || ""}`}
            >
              <span>{formatLabel(row?.[keyName] || row?.label)}</span>
              <strong>{formatNumber(row?.[valueName])}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="admin-analytics-panel__empty">
          {aa("no_data_for_period", "No data for this period.")}
        </p>
      )}
    </Panel>
  );
}

export function FlagList({ flags }) {
  const activeFlags = asArray(flags).filter((flag) => flag?.active);
  if (activeFlags.length === 0) {
    return <span className="admin-analytics-muted">{aa("none", "None")}</span>;
  }

  return (
    <div className="admin-analytics-flags">
      {activeFlags.map((flag) => (
        <span key={flag.label}>{flag.label}</span>
      ))}
    </div>
  );
}

export function SectionGrid({ children, wide = false }) {
  return (
    <div
      className={`admin-analytics-grid ${
        wide ? "admin-analytics-grid--wide" : ""
      }`.trim()}
    >
      {children}
    </div>
  );
}
